import { useEffect } from "react";
import { useLiveUI } from "@circle-react/components/LiveStreams/Room/store";

// Define types for webkit prefixed methods
interface WebkitDocument extends Document {
  webkitExitFullscreen?: () => Promise<void>;
  webkitFullscreenElement?: Element | null;
}

interface WebkitHTMLElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void>;
}

// Cast document and documentElement to include webkit prefixed methods
const doc = document as WebkitDocument;
const docEl = document.documentElement as WebkitHTMLElement;

// Bind methods to avoid 'this' scoping issues
const requestFullscreen =
  docEl.requestFullscreen?.bind(docEl) ||
  docEl.webkitRequestFullscreen?.bind(docEl);

const exitFullscreen =
  doc.exitFullscreen?.bind(doc) || doc.webkitExitFullscreen?.bind(doc);

// Assign the bound methods
docEl.requestFullscreen = requestFullscreen;
doc.exitFullscreen = exitFullscreen;

const isFullscreenMode = () =>
  !!(doc.fullscreenElement || doc.webkitFullscreenElement);

export const useFullscreenListener = () => {
  const { setIsFullscreen } = useLiveUI();

  useEffect(() => {
    const handleFullScreenChange = () => setIsFullscreen(isFullscreenMode());
    document.addEventListener("fullscreenchange", handleFullScreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullScreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullScreenChange,
      );
    };
  }, [setIsFullscreen]);
};
